import { useEffect, useMemo, useRef, useState } from 'react';
import { MdCheckCircle, MdClose } from 'react-icons/md';
import { formatLegalTypeLabel } from '../register.utils';
import styles from '../styles/LegalDocumentModal.module.css';

const LegalDocumentModal = ({ document: legalDocument, open, onClose, onConfirmRead }) => {
    const contentRef = useRef(null);
    const [hasReachedBottom, setHasReachedBottom] = useState(false);

    const title = useMemo(() => {
        if (!legalDocument) return 'Dokumen Legal';
        return legalDocument.title || formatLegalTypeLabel(legalDocument.type);
    }, [legalDocument]);

    useEffect(() => {
        if (!open) {
            setHasReachedBottom(false);
            return;
        }

        const body = window.document?.body;
        if (!body) return;

        const previousOverflow = body.style.overflow;
        body.style.overflow = 'hidden';

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose?.();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        const rafId = window.requestAnimationFrame(() => {
            const el = contentRef.current;
            if (!el) return;

            const isShortContent = el.scrollHeight <= el.clientHeight + 8;
            setHasReachedBottom(isShortContent);
        });

        return () => {
            window.cancelAnimationFrame(rafId);
            window.removeEventListener('keydown', handleKeyDown);
            body.style.overflow = previousOverflow;
        };
    }, [open, onClose]);

    useEffect(() => {
        const el = contentRef.current;
        if (!el || !open) return;

        const handleScroll = () => {
            const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 12;

            if (reachedBottom) {
                setHasReachedBottom(true);
            }
        };

        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, [open]);

    if (!open) return null;

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose?.();
        }
    };

    return (
        <div className={styles.modalOverlay} role="presentation" onMouseDown={handleBackdropClick}>
            <div
                className={styles.modalCard}
                role="dialog"
                aria-modal="true"
                aria-labelledby="legal-document-title"
                aria-describedby="legal-document-description"
            >
                <header className={styles.modalHeader}>
                    <div className={styles.headerTextWrap}>
                        <p className={styles.modalKicker}>Dokumen Legal</p>

                        <h3 id="legal-document-title" className={styles.modalTitle}>
                            {title}
                        </h3>

                        <p id="legal-document-description" className={styles.modalMeta}>
                            {legalDocument?.version ? (
                                <>
                                    Versi <strong>{legalDocument.version}</strong>
                                    {legalDocument?.published_at ? (
                                        <>
                                            {' '}
                                            • Dipublikasikan{' '}
                                            {new Date(legalDocument.published_at).toLocaleDateString('id-ID', {
                                                day: '2-digit',
                                                month: 'long',
                                                year: 'numeric',
                                            })}
                                        </>
                                    ) : null}
                                </>
                            ) : (
                                'Baca dokumen dengan seksama sebelum menyetujui.'
                            )}
                        </p>
                    </div>

                    <button type="button" className={styles.modalCloseBtn} onClick={onClose} aria-label="Tutup modal">
                        <MdClose size={22} />
                    </button>
                </header>

                <div className={styles.modalBody}>
                    <div ref={contentRef} className={styles.modalScrollableContent}>
                        {legalDocument?.content ? (
                            <article
                                className={styles.legalContent}
                                dangerouslySetInnerHTML={{ __html: legalDocument.content }}
                            />
                        ) : (
                            <div className={styles.modalEmptyState}>
                                <h4>Dokumen belum tersedia</h4>
                                <p>Silakan coba lagi beberapa saat atau hubungi administrator.</p>
                            </div>
                        )}
                    </div>
                </div>

                <footer className={styles.modalFooter}>
                    <div className={styles.footerInfo}>
                        <p className={styles.modalHint}>
                            Gulir hingga akhir dokumen untuk mengaktifkan tombol konfirmasi.
                        </p>

                        <div className={styles.progressWrap} aria-hidden="true">
                            <span
                                className={`${styles.progressDot} ${hasReachedBottom ? styles.progressDotActive : ''}`}
                            />
                            <span className={styles.progressText}>
                                {hasReachedBottom ? 'Dokumen sudah dibaca' : 'Belum selesai dibaca'}
                            </span>
                        </div>
                    </div>

                    <div className={styles.modalActions}>
                        <button type="button" className={styles.secondaryBtn} onClick={onClose}>
                            Tutup
                        </button>

                        <button
                            type="button"
                            className={styles.primaryBtn}
                            disabled={!hasReachedBottom}
                            onClick={onConfirmRead}
                        >
                            <MdCheckCircle size={18} />
                            Saya sudah membaca
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default LegalDocumentModal;
